import { findQuestionsToClear, getOtherTextPair, choiceRowOverride, OTHER_OPTION_ID } from "@idwal/checklist-visibility";
import { getSurveyorAnswers, getInspectionsList, getInspectionRecord, getReviewAnswers, updateReviewAnswer } from "../../services/answerService";
import {
    setSurveyorAnswers,
    setSurveyorLoading,
    setSurveyorError,
    setReviewAnswers,
    upsertReviewAnswer,
    removeReviewAnswers,
    markEditFailed,
    clearEditFailed,
    setSelectedInspection,
    setError,
    setInspections,
    setLoading,
    setLoadingInspections,
    setErrorInspections,
} from "../slices/answerSlice";
import { AppThunk } from "../store";
import { ReviewAnswer, ReviewAnswerUpdate } from "../../types/reviewAnswer";
import { Question } from "../../types/checklist";
import { selectChecklistConfig } from "../selectors/checklistSelectors";
import {
    buildVisibilityInputs,
    formatReviewAnswerValue,
    normaliseQuestionName,
    questionNamesForQuestions,
} from "../../utils/reviewAnswerMatcher";

// Loading/error state is deliberately separate from the review-answers fetch:
// both run on page load, and a shared flag would let whichever finishes first
// un-spinner the other tab's still-loading table.
export const fetchSurveyorAnswers = (inspectionId: string): AppThunk => async (dispatch) => {
    dispatch(setSurveyorLoading(true));
    dispatch(setSurveyorError(false));

    try {
        const response = await getSurveyorAnswers(inspectionId);

        if (response) {
            dispatch(setSurveyorAnswers(response));
        } else {
            throw Error('no data');
        }
    } catch (e) {
        console.error(e);
        dispatch(setSurveyorError(true));
    } finally {
        dispatch(setSurveyorLoading(false));
    }
};

export const fetchSelectedInspection = (inspectionId: string): AppThunk => async (dispatch) => {
    dispatch(setLoadingInspections(true));

    try {
        const record = await getInspectionRecord(inspectionId);
        dispatch(setSelectedInspection(record || undefined));
    } catch (e) {
        console.error(e);
        dispatch(setSelectedInspection(undefined));
    } finally {
        dispatch(setLoadingInspections(false));
    }
};

export const fetchReviewAnswers = (inspectionId: string): AppThunk => async (dispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(false));

    try {
        const response = await getReviewAnswers(inspectionId);

        if (response) {
            dispatch(setReviewAnswers(response));
        } else {
            throw Error('no data');
        }
    } catch (e) {
        console.error(e);
        dispatch(setError(true));
    } finally {
        dispatch(setLoading(false));
    }
};

/**
 * Persist a tech-review edit to a single response. A parent edit can hide a
 * dependent branch, so we also collect the descendant answers it orphans and
 * hard-delete them from the review copy.
 *
 * The edit is keyed by the question's identifier — its dataDictionaryId, or the
 * checklist question id for config-driven checklists. Both key the same
 * "inspection" table the same way, so there is one write path.
 *
 * The edit is applied optimistically. On a failed save the optimistic value is
 * kept on screen and the row is flagged so the reviewer can re-save it — a
 * reload re-pulls backend truth and clears the flag.
 */
export const saveReviewAnswer = (
    inspectionId: string,
    update: ReviewAnswerUpdate
): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const config = selectChecklistConfig(state);
    const reviewAnswers = state.answer.reviewAnswers;

    // DD-bound answers key by dataDictionaryId; config answers by their question id.
    const identifier = update.dataDictionaryId ?? update.questionId;
    if (!identifier) return;

    const answersByName = new Map<string, ReviewAnswer>();
    reviewAnswers.forEach((answer) => answersByName.set(answer.questionName, answer));

    // Resolve the edited question from the checklist. For multi-choice we also
    // need its full option set — the backend deletes the deselected option rows
    // by exact key, so it must be told every option id.
    const { allQuestions, answersById } = config
        ? buildVisibilityInputs(config, answersByName)
        : { allQuestions: [] as Question[], answersById: {} as Record<string, { value: unknown }> };
    const question = allQuestions.find((q) => (q.dataDictionaryId ?? q.id) === identifier);
    const isMultiChoice = (question?.type ?? update.questionType) === "multi-choice";
    const options = question?.options ?? [];
    const optionIds = isMultiChoice ? options.map((option) => option.id) : undefined;

    // Descendant answers orphaned by this edit (their branch is no longer shown).
    // Empty for a same-branch edit or when no checklist is loaded.
    let clears: string[] = [];
    if (question) {
        const orphanQuestions = findQuestionsToClear(question.id, update.value, allQuestions, answersById)
            .map((id) => allQuestions.find((q) => q.id === id))
            .filter((q): q is Question => q !== undefined);
        clears = questionNamesForQuestions(orphanQuestions, reviewAnswers);
    }

    // Optimistically apply the edit. Multi-choice is one row per selected option
    // (mirrors the backend), so replace the question's option rows wholesale.
    if (isMultiChoice) {
        const selected = new Set(update.value as string[]);
        dispatch(removeReviewAnswers(options.map((option) => `${identifier}_${option.id}`)));
        for (const option of options) {
            if (selected.has(option.id)) {
                dispatch(upsertReviewAnswer({
                    questionName: `${identifier}_${option.id}`,
                    value: option.text,
                    answer: "",
                    answerType: 0,
                    isNotable: false,
                }));
            }
        }
    } else {
        const formattedValue = formatReviewAnswerValue(update.value, question?.type ?? update.questionType ?? undefined);
        // Mirror the backend's notable rule: a non-empty _details field is notable.
        // Config identifiers never end in _details, so their rows stay answer_type 0.
        const isNotable = identifier.endsWith("_details") && formattedValue !== "";
        const questionName = normaliseQuestionName(identifier);
        dispatch(upsertReviewAnswer({
            questionName,
            value: formattedValue,
            // Preserve any existing notable base value; it isn't recomputed here.
            answer: answersByName.get(questionName)?.answer ?? "",
            answerType: isNotable ? 1 : 0,
            isNotable,
        }));

        // A choice on Other can hold its typed Other text in its own row, and the
        // backend rewrites that row on either half's edit (see otherTextRules in
        // @idwal/checklist-visibility). Mirror it, so the optimistic state reads
        // back as a refetch will.
        const otherTextPair = getOtherTextPair(identifier);
        const editsOtherText = otherTextPair?.otherText === identifier;
        if (otherTextPair && (editsOtherText || update.value === OTHER_OPTION_ID)) {
            const choiceRowName = normaliseQuestionName(otherTextPair.choice);
            const otherTextValue = editsOtherText
                ? update.value
                : answersByName.get(normaliseQuestionName(otherTextPair.otherText))?.value;
            dispatch(upsertReviewAnswer({
                questionName: choiceRowName,
                value: choiceRowOverride(OTHER_OPTION_ID, otherTextValue)
                    ?? formatReviewAnswerValue(OTHER_OPTION_ID, "single-choice"),
                answer: answersByName.get(choiceRowName)?.answer ?? "",
                answerType: 0,
                isNotable: false,
            }));
        }
    }
    if (clears.length > 0) {
        dispatch(removeReviewAnswers(clears));
    }

    // Optimistically assume success — clear any prior failed flag for this row.
    dispatch(clearEditFailed(identifier));

    const ok = await updateReviewAnswer(inspectionId, update, clears, optionIds);
    if (!ok) {
        // Keep the optimistic edit; flag the row so the reviewer can re-save
        // (the service already surfaced a toast).
        dispatch(markEditFailed(identifier));
    }
};

export const fetchInspectionsList = (): AppThunk => async (dispatch) => {
    dispatch(setLoadingInspections(true));
    dispatch(setErrorInspections(false));

    try {
        const response = await getInspectionsList();

        if (response) {
            dispatch(setInspections(response));
        } else {
            throw Error('no data');
        }
    } catch (e) {
        console.error(e);
        dispatch(setErrorInspections(true));
    } finally {
        dispatch(setLoadingInspections(false));
    }
};
